import {
  SelectPattern,
  ActionType,
  RunningPattern,
  BudgetType,
} from 'core/rtbCampaign/singlePage/common';
import { OutBound } from 'core/brand/Brand';
import { DefaultBrandManager } from 'core/brand/BrandManager';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useCampaignStore } from 'store/campaignStore';
import { Goal, OutboundInfo } from './useKeywordForm';
import { useCoreContext } from 'contexts/coreContext';
import { OutBound_default_AdContent } from '../Components/Keyword/Outbound/Outbound';
import { useProductStore } from 'store/productStore';
import { numberWithCommas } from 'utils/StringUtil';
import { confirm } from '@rsuite/interactions';
import _ from 'lodash';
import { AdType } from 'core/rtbCampaign/RtbCampaign';
interface OutboundMetaProps {
  defaultForm: any;
  values: ReturnType<typeof useFormik>['values'];
  diffDays?: number;
  selectBrand?: string | null;
  setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
}

const defaultBrandManager = new DefaultBrandManager();

const useOutboundMeta = (
  initialState: OutboundMetaProps = {
    defaultForm: {},
    values: {},
    diffDays: 0,
    selectBrand: null,
    setFieldValue: async () => Promise.resolve(),
  },
) => {
  const { defaultForm, values, diffDays, selectBrand, setFieldValue } =
    initialState;
  const core = useCoreContext();
  const { actionType, showProductManager, openProductManager } =
    useCampaignStore();
  const { selectedAutoProducts, selectedProducts } = useProductStore();
  const outsideMetaPermission: boolean = _.get(
    core,
    'accountManager.localeMeta.addonFeatures.campaign.outsideMeta',
    false,
  );

  // 是否可使用站外聯播網
  const [enAbledOutboundSection, setEnAbledOutboundSection] = useState(false);
  // Meta 廣告投放帳號
  const [outboundBrandOptions, setOutboundBrandOptions] = useState<OutBound[]>(
    [],
  );
  // Meta 不符合投遞條件的提示
  const [outboundHiteText, setOutboundHiteText] = useState('');
  const [didFetch, setDidFetch] = useState(false);

  // 判斷是否可切換站外聯播網
  const limitedOutbound = () => {
    if (
      defaultForm?.basic.outboundInfo?.brandNo?.length > 0 &&
      !defaultForm?.basic.enableOutbound
    ) {
      return true;
    } else if (
      defaultForm?.basic.outboundInfo?.brandNo?.length > 0 &&
      defaultForm?.basic.enableOutbound
    ) {
      return false;
    } else {
      return false;
    }
  };

  /* 站外聯播網-Meta */
  // 取得 站外聯播網 下拉選單
  const fetchOusideBrandOptions = async (products) => {
    try {
      const goodsId = products?.map((item) => item.productId)?.join(',') || '';
      setDidFetch(false);
      const options = await defaultBrandManager.getOutSideBrands({
        campaignAdType: AdType.MAX_CVR_KEYWORD,
        goodsId: goodsId,
      });

      setOutboundBrandOptions(options);

      return options;
    } catch (error) {
      console.error('Failed to fetch data:', error);
      return []; // 確保回傳 options
    } finally {
      setDidFetch(true);
    }
  };

  // 修改站外聯播網品牌
  const handleChangOutbound = (selectedOption: any) => {
    const selected = outboundBrandOptions?.find(
      (item) => item.value === selectedOption,
    );

    // 寫入站外資料
    handleSetOutboundInfo({
      brandNo: selectedOption,
      brandName: selected?.label || '',
      name: selected?.label || '',
      logoUrl: selected?.logoUrl || '',
      adContent: OutBound_default_AdContent,
      segment: values?.basic?.outboundInfo?.segment || [],
      goal: values?.basic?.outboundInfo?.goal || '',
    });
  };

  // 設定站外資料(獨立寫是因為很多地方共用)
  const handleSetOutboundInfo = (outboundInfo: OutboundInfo) => {
    const { brandNo, brandName, name, logoUrl, adContent, goal } = outboundInfo;
    setFieldValue('basic.outboundInfo.brandNo', brandNo);
    setFieldValue('basic.outboundInfo.brandName', brandName);
    setFieldValue('basic.outboundInfo.name', `${name} - momo購物網`);
    setFieldValue('basic.outboundInfo.logoUrl', logoUrl);
    setFieldValue('basic.outboundInfo.adContent', adContent);
    setFieldValue('basic.outboundInfo.segment', outboundInfo.segment || []);
    setFieldValue('basic.outboundInfo.goal', goal || '');
  };
  // 清除站外資料
  const handleResetOutboundInfo = () => {
    if (!defaultForm?.basic?.outboundInfo?.brandNo) {
      handleSetOutboundInfo({
        brandNo: '',
        brandName: '',
        name: '',
        logoUrl: '',
        adContent: OutBound_default_AdContent,
        segment: [],
        goal: '',
      });
    }
  };

  // 變更 Meta廣告主要文字
  const handleSetMetaContent = (text) => {
    setFieldValue('basic.outboundInfo.adContent', text);
  };

  const handleGoalChange = (goal: Goal) => {
    setFieldValue('basic.outboundInfo.goal', goal);
  };

  const handleSegmentChange = (segment: string[]) => {
    const filteredSegment = segment.filter((s) => s !== 'categoryRoot');
    setFieldValue('basic.outboundInfo.segment', filteredSegment);
  };

  const handleCloseProductManager = async () => {
    // 沒有開起站外聯播網就不用往下跑
    if (outsideMetaPermission === false) {
      openProductManager(false);
      return;
    }

    if (actionType === ActionType.CREATE) {
      handleResetOutboundInfo();
      openProductManager(false);
      return;
    }

    // 站外有開就要驗證 Meta投放帳號 是否足兩品
    if (
      actionType === ActionType.EDIT &&
      defaultForm?.basic?.enableOutbound === true &&
      values?.basic?.enableOutbound === true
    ) {
      const target = selectedProducts?.filter(
        (item) => item.brandId === values?.basic?.outboundInfo?.brandNo,
      );

      if (!target || target?.length < 2) {
        const confirmOptions = {
          okButtonText: '確認關閉',
          cancelButtonText: '取消',
        };
        const result = await confirm(
          'momo聯播網帳號可投遞商品不足2品，是否要關閉momo聯播網投放',
          confirmOptions,
        );

        // 確認關閉，清除站外資料
        if (result) {
          setFieldValue('basic.enableOutbound', false);
          handleResetOutboundInfo();
          openProductManager(false);
          return;
        } else {
          // 不關閉
          return;
        }
      }
      openProductManager(false);
      return;
    }

    openProductManager(false);
  };

  const handleChangeToggle = (checked) => {
    if (values?.basic?.adType !== AdType.OUTBOUND_KEYWORD) {
      setFieldValue('basic.enableOutbound', checked);
    }
    if (
      values?.basic?.webBrandNo?.length > 0 &&
      values?.basic?.selectPattern === SelectPattern.AUTO
    ) {
      const target = outboundBrandOptions?.find(
        (item) => item.value === values?.basic?.webBrandNo,
      );
      if (target) {
        handleSetOutboundInfo({
          brandNo: target.webBrandNo,
          brandName: target.brandName,
          name: target.brandName,
          logoUrl: target.logoUrl,
          adContent:
            values?.basic?.outboundInfo?.adContent ||
            OutBound_default_AdContent,
          segment: values?.basic?.outboundInfo?.segment || [],
          goal: values?.basic?.outboundInfo?.goal || '',
        });
      }
    }

    if (!defaultForm?.basic?.outboundInfo?.brandNo && checked === false) {
      // 清空站外資料
      handleResetOutboundInfo();
    }
  };

  const handleCheckIsAllOther = (products) => {
    if (products?.length === 0) return false;

    const others =
      products?.filter((product) => product.brandId === '99999999999999999') ||
      [];
    // 如果 "其他" 的品牌數量等於全部的品牌數量，就是全部都是 "其他"
    if (others.length === products.length) return true;
    return false;
  };

  const handleEnableOutbound = ({ values, diffDays = 1 }) => {
    if (values?.basic?.adType === AdType.OUTBOUND_KEYWORD) return;

    const {
      runningPattern,
      budgetType,
      budget,
      dailyTargetBudget,
      selectPattern,
      webBrandNo,
      enableOutbound,
    } = values.basic;

    // 站外驗證失敗要關閉站外聯播網
    const handleDisable = (message: string) => {
      setEnAbledOutboundSection(false);
      setFieldValue('basic.enableOutbound', false);
      setOutboundHiteText(message);
      handleResetOutboundInfo();
    };

    // 站外驗證成功可以開啟站外聯播網
    const handleEnable = () => {
      setEnAbledOutboundSection(true);
      setOutboundHiteText('');
    };

    // 檢查品牌是否存在
    const checkBrand = (brandNo: string, searchBy = 'value') => {
      const target = outboundBrandOptions.find(
        (item) => item[searchBy] === brandNo,
      );
      return target ? target : null;
    };

    // 設置站外信息
    const enableOutboundWithBrand = (target: any) => {
      handleEnable();
      setFieldValue('basic.enableOutbound', true);
      handleSetOutboundInfo({
        brandNo: target.webBrandNo,
        brandName: target.brandName,
        name: target.brandName,
        logoUrl: target.logoUrl,
        adContent:
          values?.basic?.outboundInfo?.adContent || OutBound_default_AdContent,
        segment: values?.basic?.outboundInfo?.segment || [],
        goal: values?.basic?.outboundInfo?.goal || '',
      });
    };

    // 基本驗證
    if (!outsideMetaPermission) return;

    if (runningPattern !== RunningPattern.LIMITED) {
      return handleDisable('若要開啟momo聯播網，請設定廣告開始/結束日期');
    }
    const minBudget = 200 * diffDays;
    //每日預算需大於200
    if (
      budgetType === BudgetType.DAILY &&
      !defaultForm?.basic?.outboundInfo?.brandNo &&
      dailyTargetBudget < 200
    ) {
      return handleDisable('若要開啟momo聯播網，請設定日預算至少 NTD 200');
    }
    //總預算需大於300 * 設定天數
    if (
      budgetType === BudgetType.TOTAL &&
      !defaultForm?.basic?.outboundInfo?.brandNo &&
      budget < minBudget
    ) {
      return handleDisable(
        `若要開啟momo聯播網，請設定總預算至少 NTD ${numberWithCommas(minBudget)}`,
      );
    }

    // 檢查品牌選項
    const hasOnlyOtherBrand =
      outboundBrandOptions?.length === 1 &&
      outboundBrandOptions[0]?.value === '99999999999999999';

    if (hasOnlyOtherBrand) {
      return handleDisable('您的供應商帳號下無可選品牌，無法開啟momo聯播網');
    }

    const products =
      selectPattern === SelectPattern.AUTO
        ? selectedAutoProducts
        : selectedProducts;

    if (products.length > 0 && handleCheckIsAllOther(products)) {
      return handleDisable('您的供應商帳號下無可選品牌，無法開啟momo聯播網');
    }

    // 處理創建/複製模式

    if (actionType === ActionType.CREATE) {
      if (!enableOutbound && (!products || products.length === 0)) {
        return handleDisable('您的供應商帳號下無可選品牌，無法開啟momo聯播網');
      }

      if (selectPattern === SelectPattern.AUTO) {
        if (webBrandNo?.length) {
          const target = checkBrand(webBrandNo);
          if (!target) {
            return handleDisable(
              '您的供應商帳號下無可選品牌，無法開啟momo聯播網',
            );
          }
          return enableOutboundWithBrand(target);
        } else {
          handleEnable();
          setFieldValue('basic.enableOutbound', true);
        }
      } else if (selectPattern === SelectPattern.MANUAL) {
        if (!outboundBrandOptions?.length) {
          return handleDisable(
            '您的供應商帳號下無可選品牌，無法開啟momo聯播網',
          );
        }
        handleEnable();
        setFieldValue('basic.enableOutbound', true);
      }
      return;
    }

    // 處理編輯模式
    if (actionType === ActionType.EDIT || actionType === ActionType.COPY) {
      const hasExistingOutbound = defaultForm.basic.outboundInfo?.brandNo;

      if (hasExistingOutbound) {
        const defaultBrandNo = defaultForm.basic.outboundInfo.brandNo;

        if (selectPattern === SelectPattern.AUTO) {
          const exists = outboundBrandOptions.some(
            (item) => item.webBrandNo === defaultBrandNo,
          );
          return exists
            ? handleEnable()
            : handleDisable('您投放的品牌已下架，無法開啟momo聯播網');
        } else {
          const target = checkBrand(defaultBrandNo, 'webBrandNo');
          if (!target) {
            return handleDisable(
              '您的供應商帳號下無可選品牌，無法開啟momo聯播網',
            );
          }
          return target.available
            ? handleEnable()
            : handleDisable(
                '該品牌下您所選擇的商品不足2品，無法投遞 Meta 廣告',
              );
        }
      } else {
        // 沒有現有站外設定的編輯模式
        if (!outboundBrandOptions?.length) {
          return handleDisable(
            '您的供應商帳號下無可選品牌，無法開啟momo聯播網',
          );
        }

        if (selectPattern === SelectPattern.AUTO && webBrandNo?.length) {
          const target = checkBrand(webBrandNo);
          if (!target) {
            return handleDisable(
              '您的供應商帳號下無可選品牌，無法開啟momo聯播網',
            );
          }
          return target.available
            ? handleEnable()
            : handleDisable(
                '該品牌下您所選擇的商品不足2品，無法投遞 Meta 廣告',
              );
        }

        if (
          selectPattern === SelectPattern.MANUAL &&
          values?.basic.outboundInfo?.brandNo?.length
        ) {
          const target = checkBrand(
            values.basic.outboundInfo.brandNo,
            'webBrandNo',
          );
          return target?.available ? handleEnable() : handleResetOutboundInfo();
        }

        handleEnable();
      }
    }
  };

  // 站外取得 Meta 廣告投放帳號
  useEffect(() => {
    // 有開站外才要fetch
    if (!outsideMetaPermission) return;
    // 已經開過站外又關閉不用fetch
    if (
      actionType === ActionType.EDIT &&
      defaultForm?.basic?.outboundInfo?.brandNo.length > 0 &&
      limitedOutbound()
    ) {
      setDidFetch(true);
      return;
    }

    const products =
      values?.basic?.selectPattern === SelectPattern.AUTO
        ? selectedAutoProducts
        : selectedProducts;

    if (products.length > 0 && !showProductManager) {
      // 進入編輯時, 撈取 Meta 廣告投放帳號
      fetchOusideBrandOptions(products);
    } else {
      setDidFetch(true);
    }
  }, [
    selectedAutoProducts,
    selectedProducts,
    values?.basic?.selectPattern,
    showProductManager,
    defaultForm?.basic?.outboundInfo?.brandNo,
    // values?.basic?.outboundInfo?.segment
  ]);

  // 驗證站外是否能開啟
  useEffect(() => {
    // 有開站外才要驗證
    if (!outsideMetaPermission) return;
    // 手動選品中不用驗證
    if (showProductManager === true) return;

    // 已經開過站外又關閉不用fetch
    if (
      actionType === ActionType.EDIT &&
      defaultForm?.basic?.outboundInfo?.brandNo.length > 0 &&
      limitedOutbound()
    ) {
      return;
    }

    if (didFetch) {
      handleEnableOutbound({ values, diffDays });
    }
  }, [
    outsideMetaPermission,
    actionType,
    diffDays,
    selectedAutoProducts,
    selectedProducts,
    showProductManager,
    outboundBrandOptions,
    defaultForm?.basic?.outboundInfo?.brandNo,
    values?.basic?.budgetType,
    values?.basic?.budget,
    values?.basic?.dailyTargetBudget,
    values?.basic?.selectPattern,
    values?.basic?.runningPattern,
    values?.basic?.webBrandNo,
    didFetch,
  ]);

  useEffect(() => {
    if (!outsideMetaPermission) return;

    // 清空選品模式變更時的站外資訊
    if (!defaultForm?.basic?.outboundInfo?.brandNo) {
      setOutboundBrandOptions([]);
    }

    // 當為 create 模式或 selectBrand 改變時，重置站外資訊
    if (actionType === ActionType.CREATE) {
      handleResetOutboundInfo();
    }
  }, [
    outsideMetaPermission,
    actionType,
    values?.basic?.selectPattern,
    selectBrand,
  ]);

  return {
    didFetch,
    outboundBrandOptions,
    enAbledOutboundSection,
    outboundHiteText,
    outsideMetaPermission,
    handleChangOutbound,
    handleSetMetaContent,
    handleGoalChange,
    handleSegmentChange,
    handleChangeToggle,
    handleCloseProductManager,
    limitedOutbound,
  };
};

export default useOutboundMeta;
